import React from "react";
import { useNavigate } from "react-router-dom";
import { FiShield, FiEye, FiChevronRight, FiCheckCircle, FiClock, FiUserCheck, FiCoffee, FiLock, FiExternalLink, FiShare2, FiGlobe, FiRefreshCcw, FiMail } from "react-icons/fi";

const PrivacyPolicy2 = () => {
  const navigate = useNavigate();
  return (
    <div className="tpu__splashScreen__wrapper layoutContainer"
    style={{
        padding:"1rem"
    }}
    >
      {/* Back navigation */}
      <div style={{ marginBottom: "12px" }}>
        <button
          onClick={() => navigate("/")}
          style={{
            background: "rgba(0, 0, 0, 0.05)",
            border: "none",
            borderRadius: "6px",
            padding: "6px 12px",
            color: "#3797F0",
            fontSize: "13px",
            fontWeight: "600",
            cursor: "pointer",
          }}
        >
          ← Back to Home
        </button>
      </div>

      {/* Header */}
      <div className="header">
        <FiShield size={28} color="#3797F0" />
        <h2 className="title">Privacy Policy - TaskPlanet</h2>
      </div>

      <p className="date">Last Updated: 21 Nov 2025</p>
      <a href="https://www.taskplanet.org/" target="_blank" rel="noreferrer" className="link">
        www.taskplanet.org
      </a>

      <p className="paragraph">
        At Task Planet, accessible from www.taskplanet.org, your privacy is one
        of our top priorities. This Privacy Policy outlines how we collect, use,
        disclose, and safeguard your personal data in accordance with the Digital
        Personal Data Protection Act, 2023 (DPDP Act), India. By using our
        website, you consent to the collection and use of your information as
        described in this policy.
      </p>

      {/* Sections */}
      <Section
        title="1. Data We Collect"
        items={[
          "Your IP address",
          "Contact information, including email address and phone number",
          "Demographic information, interests, and preferences",
          "Behavioural data related to your use of our website",
          "Technical data such as browser type and operating system",
          "Non-personal information which does not identify you directly",
          "We collect and use device identifiers such as Advertising ID (AAID), cookies, IP address, app activity and device information to deliver, optimize and measure ads."
        ]}
        content="When you visit or interact with our website, we may collect the following categories of personal data:"
      />

      <Section
        title="2. Purpose of Data Collection"
        items={[
          "To provide and maintain our services",
          "To personalize your user experience",
          "To send relevant promotional content (based on your consent)",
          "To conduct surveys and market research",
          "To enhance website security and prevent fraud",
          "To comply with legal obligations",
          "We use Google AdMob and other third-party advertising partners to show ads inside our app.",
          "These partners may collect device identifiers, ad interactions, and diagnostic information to deliver personalized or non-personalized advertising."
        ]}
        content="We collect and process your data for the following lawful purposes"
        bottomContent="All data processing activities are carried out in accordance with the principles of purpose limitation, data minimization, and accuracy under the DPDP Act."
      />

      <Section
        title="3. Legal Basis for Processing"
        items={[
          "Consent: When you voluntarily provide your data and opt-in to services or communication",
          "Performance of a Contract: When data is required to deliver services you request",
          "Legal Obligation: When processing is necessary to comply with applicable laws",
          "Legitimate Interests: When used to improve our offerings, provided it does not override your rights",
        ]}
        content="Your personal data is processed under one or more of the following lawful bases:"
      />

      <SimpleSection
        title="4. Data Retention"
        text="We retain your personal data only for as long as necessary to fulfil the purposes outlined in this policy or as required by law. Once data is no longer needed, it is securely deleted or anonymized."
      />

      <Section
        title="5. User Rights"
        items={[
          "Access your personal data",
          "Correct inaccurate or incomplete data",
          "Withdraw Consent at any time (where consent is the basis for processing)",
          "Nominate a person in case of incapacity or death",
          "File a complaint with the Data Protection Board of India",
          "You may request deletion of your personal data, subject to applicable legal requirements",
          "We provide a Consent Management Platform (CMP) that allows users to manage their advertising preferences, including granting or denying consent for personalized ads.",
          "Depending on your consent, we may show either personalized ads (based on your interests and app activity) or non-personalized ads. You can deny or withdraw consent at any time through our in-app privacy settings."
        ]}
      />
      <p className="paragraph">
        You can exercise these rights by contacting us at:{" "}
        <a href="mailto:support@taskplanet.org" className="link">
          support@taskplanet.org
        </a>
      </p>

      <Section
        title="6. Cookies and Tracking"
        items={[
          "We use cookies to enhance your browsing experience, analyze traffic, and serve relevant content. By accepting cookies on our site, you consent to their use. You may disable cookies via your browser settings. For detailed instructions, visit www.taskplanet.org/cookies.",
          "We and our advertising partners use cookies, SDKs and tracking technologies to understand how the app is used, improve performance, and deliver relevant ads."
        ]}
      />

      <SimpleSection
        title="7. Data Security"
        text="Task Planet is committed to ensuring your data is secure. We have implemented appropriate physical, technical, and administrative safeguards to prevent unauthorized access, data breaches, and other threats."
      />

      <SimpleSection
        title="8. Third-Party Links"
        text="Our website may contain links to external websites. Task Planet is not responsible for the content or privacy practices of such third parties. We encourage you to read their privacy policies before providing personal information."
      />

      <Section
        title="9. Data Sharing and Transfer"
        content="We do not sell or lease your personal data to any third parties. We may share your data only:"
        items={[
          "With service providers or partners who assist in delivering our services (under strict confidentiality)",
          "If required by law, regulation, or legal process",
          "With your explicit consent",
          "We may share limited data (such as Advertising IDs and app usage information) with our advertising partners strictly for ad delivery, frequency capping, fraud prevention, analytics and reporting."
        ]}
      />
      <p className="paragraph">
        If your data is transferred outside India, it will be done in accordance
        with applicable Indian law and with adequate safeguards.
      </p>

      <Section
        title="10. For Global Compliance"
        items={[
          "While this policy is primarily designed in accordance with the Digital Personal Data Protection Act, 2023 (DPDP Act) of India, Task Planet also applies internationally recognized data protection principles (such as GDPR standards) to safeguard the rights of our global users.",
          "Users in the European Economic Area (EEA), UK and regions where required will be shown a consent message before personalized ads are used. ",
          "We follow the IAB Transparency & Consent Framework (TCF) for transmitting consent signals to our advertising partners.",
        ]}
      />

      <SimpleSection
        title="11. Updates to This Policy"
        text="We may update this Privacy Policy from time to time. Any changes will be reflected on this page with the 'Effective Date' updated accordingly. You are encouraged to review this page periodically."
      />

      <Section
        title="12. Children’s Policy"
        items={[
          "We do not knowingly collect personal data from children under the age of 18 without the consent of a parent or legal guardian.",
          "If you are under 18, please do not provide your personal information without parental consent.",
          "If we become aware that we have collected personal data from a child without proper consent, we will take steps to delete it immediately.",

        ]}
      />
       <p className="paragraph">
        Parents and guardians may contact us at:{" "}
        <a href="mailto:support@taskplanet.org" className="link">
          support@taskplanet.org
        </a>
       {" "} to review, correct, or request deletion of their child’s data.
      </p>

      <SimpleSection
        title="13. Contact Us"
        text="If you have questions or concerns about this Privacy Policy or wish to exercise your rights, please contact us at: support@taskplanet.org"
      />
    </div>
  );
};

// Reusable Section Component
const Section = ({ title, content, items, bottomContent }) => (
  <div className="section">
    <h3 style={{
        textAlign: "left"
    }} className="section-title">{title}</h3>

    {content && <p className="section-content">{content}</p>}

    {items?.map((item, i) => (
      <p key={i} className="list-item">• {item}</p>
    ))}

    {bottomContent && <p className="section-content">{bottomContent}</p>}
  </div>
);

const SimpleSection = ({ title, text }) => {
  const emailRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
  const emailMatch = text.match(emailRegex);

  return (
    <div className="section">
      <h3 style={{
            textAlign: "left"
      }} className="section-title">{title}</h3>
      {emailMatch ? (
        <p className="paragraph">
          {text.split(emailMatch[0])[0]}
          <a href={`mailto:${emailMatch[0]}`} className="link">
            {emailMatch[0]}
          </a>
          {text.split(emailMatch[0])[1]}
        </p>
      ) : (
        <p className="paragraph">{text}</p>
      )}
    </div>
  );
};

// CSS styles
const css = `
.privacy-container { max-width: 900px; margin: auto; padding: 20px; font-family: Arial, sans-serif; color: #222; }
.header { display: flex; align-items: center; margin-bottom: 12px; }
.title { font-size: 22px; font-weight: bold; margin-left: 10px; color: #222; }
.date { font-size: 14px; color: #666; margin-bottom: 4px; }
.section { margin-bottom: 20px; }
.section-title { font-size: 18px; font-weight: 600; margin-bottom: 8px; color: black; 
text-allign:center;
}
.paragraph { font-size: 14px; line-height: 20px; margin-bottom: 8px; color: #444; }
.list-item { font-size: 14px; line-height: 20px; color: #444; margin-left: 12px; }
.link { color: #3797F0; text-decoration: underline; cursor: pointer; }
.section-content { font-size: 14px; color: #555; margin-bottom: 8px; line-height: 20px; }
`;

export default PrivacyPolicy2;
